import { useMemo } from 'react';
import { cx } from '@/cva.config';
import { useNow } from '@/hooks/useNow';

interface ScheduleActivityBarProps {
  offpeakMin: number;
  peakEndHour: number;
  peakMin: number;
  peakStartHour: number;
  timezone: string;
}

interface ScanTick {
  minute: number;
  peak: boolean;
}

const MINUTES_PER_DAY = 1440;
const NOW_REFRESH_MS = 30_000;
const AXIS_TICKS = [
  { align: 'start', label: '12a', value: 0 },
  { align: 'center', label: '6a', value: 360 },
  { align: 'center', label: '12p', value: 720 },
  { align: 'center', label: '6p', value: 1080 },
  { align: 'end', label: '12a', value: MINUTES_PER_DAY },
] as const;

const hourIsPeak = (
  hour: number,
  peakStartHour: number,
  peakEndHour: number,
) => {
  if (peakStartHour === peakEndHour) {
    return true;
  }

  if (peakStartHour < peakEndHour) {
    return hour >= peakStartHour && hour < peakEndHour;
  }

  return hour >= peakStartHour || hour < peakEndHour;
};

const generateScanTicks = (
  peakStartHour: number,
  peakEndHour: number,
  peakMin: number,
  offpeakMin: number,
): ScanTick[] => {
  const ticks: ScanTick[] = [];
  const safePeak = Math.max(1, peakMin);
  const safeOffpeak = Math.max(1, offpeakMin);
  let minute = 0;

  while (minute < MINUTES_PER_DAY) {
    const peak = hourIsPeak(
      Math.floor(minute / 60) % 24,
      peakStartHour,
      peakEndHour,
    );
    ticks.push({ minute, peak });
    minute += peak ? safePeak : safeOffpeak;
  }

  return ticks;
};

const minuteOfDayInTimezone = (date: Date, timezone: string) => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hour12: false,
    hourCycle: 'h23',
    minute: '2-digit',
    timeZone: timezone,
  });
  const parts = fmt.formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);

  return ((hour % 24) * 60 + minute) % MINUTES_PER_DAY;
};

const minutesUntilNextTick = (nowMinute: number, ticks: ScanTick[]) => {
  const [first] = ticks;

  if (!first) {
    return null;
  }

  const upcoming = ticks.find((tick) => tick.minute >= nowMinute);

  if (upcoming) {
    return upcoming.minute - nowMinute;
  }

  return MINUTES_PER_DAY - nowMinute + first.minute;
};

interface PeakBand {
  endPct: number;
  startPct: number;
}

const computePeakBands = (
  peakStartHour: number,
  peakEndHour: number,
): PeakBand[] => {
  if (peakStartHour === peakEndHour) {
    return [{ endPct: 100, startPct: 0 }];
  }

  const startPct = (peakStartHour / 24) * 100;
  const endPct = (peakEndHour / 24) * 100;

  if (peakStartHour < peakEndHour) {
    return [{ endPct, startPct }];
  }

  return [
    { endPct: 100, startPct },
    { endPct, startPct: 0 },
  ];
};

export const computeNextScanMinutes = (
  now: Date,
  timezone: string,
  peakStartHour: number,
  peakEndHour: number,
  peakMin: number,
  offpeakMin: number,
) => {
  const ticks = generateScanTicks(
    peakStartHour,
    peakEndHour,
    peakMin,
    offpeakMin,
  );
  const nowMinute = minuteOfDayInTimezone(now, timezone);

  return minutesUntilNextTick(nowMinute, ticks);
};

export const useNextScanMinutes = (
  timezone: string,
  peakStartHour: number,
  peakEndHour: number,
  peakMin: number,
  offpeakMin: number,
) => {
  const now = useNow(NOW_REFRESH_MS);

  return useMemo(
    () =>
      computeNextScanMinutes(
        now,
        timezone,
        peakStartHour,
        peakEndHour,
        peakMin,
        offpeakMin,
      ),
    [now, timezone, peakStartHour, peakEndHour, peakMin, offpeakMin],
  );
};

export const ScheduleActivityBar = ({
  offpeakMin,
  peakEndHour,
  peakMin,
  peakStartHour,
  timezone,
}: ScheduleActivityBarProps) => {
  const now = useNow(NOW_REFRESH_MS);

  const ticks = useMemo(
    () => generateScanTicks(peakStartHour, peakEndHour, peakMin, offpeakMin),
    [peakStartHour, peakEndHour, peakMin, offpeakMin],
  );

  const peakBands = useMemo(
    () => computePeakBands(peakStartHour, peakEndHour),
    [peakStartHour, peakEndHour],
  );

  const nowMinute = useMemo(
    () => minuteOfDayInTimezone(now, timezone),
    [now, timezone],
  );

  const nowPct = (nowMinute / MINUTES_PER_DAY) * 100;

  return (
    <div className="relative h-8 w-full overflow-hidden rounded-md border border-border bg-surface">
      {peakBands.map((band) => (
        <div
          className="absolute inset-y-0 bg-accent-soft"
          key={`peak-${band.startPct}-${band.endPct}`}
          style={{
            left: `${band.startPct}%`,
            width: `${band.endPct - band.startPct}%`,
          }}
        />
      ))}
      {ticks.map((tick) => (
        <div
          className={cx(
            'absolute top-1/2 w-px -translate-x-1/2 -translate-y-1/2',
            tick.peak ? 'h-5 bg-accent' : 'h-2 bg-border-active',
          )}
          key={`tick-${tick.minute}`}
          style={{ left: `${(tick.minute / MINUTES_PER_DAY) * 100}%` }}
        />
      ))}
      <div
        className="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-foreground"
        style={{ left: `${nowPct}%` }}
      >
        <span className="absolute top-0 left-1/2 size-1 -translate-x-1/2 bg-foreground" />
        <span className="absolute bottom-0 left-1/2 size-1 -translate-x-1/2 bg-foreground" />
      </div>
    </div>
  );
};

export const ScheduleActivityAxis = () => (
  <div className="relative mt-1 h-4 font-mono text-xs text-subtle">
    {AXIS_TICKS.map((tick) => (
      <span
        className={cx(
          'absolute top-0',
          tick.align === 'start' && 'left-0',
          tick.align === 'center' && '-translate-x-1/2',
          tick.align === 'end' && 'right-0',
        )}
        key={`axis-${tick.value}`}
        style={
          tick.align === 'center'
            ? { left: `${(tick.value / MINUTES_PER_DAY) * 100}%` }
            : undefined
        }
      >
        {tick.label}
      </span>
    ))}
  </div>
);
